"use client";

import { useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { formatMenuItemPriceLabel } from "@/lib/menu-price-options";
import { itemRequiresCustomization } from "@/lib/menu-item-customization";
import { formatPrice } from "@/stores/cart-store";
import { useActiveCartStore } from "@/components/order/cart-scope";
import { cn } from "@/lib/utils";
import type { Doc } from "../../../convex/_generated/dataModel";

// Fallback sheet for consumers that don't lift selection to a shared,
// surface-level sheet (e.g. the catering page). Lazy so the heavy
// framer-motion/Convex chunk still only loads on first tap. Surfaces that pass
// onSelect (the ordering menu) render one shared sheet instead and never mount
// this.
const ItemCustomizationSheet = dynamic(
  () =>
    import("./item-customization-sheet").then(
      (mod) => mod.ItemCustomizationSheet,
    ),
  { ssr: false },
);

interface MenuItemCardProps {
  item: Doc<"menu_items">;
  priority?: boolean;
  /**
   * Ids of items with a required modifier group, from
   * menu.getMenuItemIdsWithRequiredModifiers. When null/undefined (browse-only
   * mode, or the set still loading) one-tap quick add stays disabled and the
   * + button opens the customization sheet exactly as before.
   */
  quickAddRequiredIds?: Set<string> | null;
  /**
   * Opens the shared, surface-level customization sheet for this item. Lifting
   * the sheet out of every card means one ItemCustomizationSheet instance (and
   * one framer-motion/Convex-query load) per surface instead of one per card.
   * When omitted, the card falls back to its own local (lazy) sheet.
   */
  onSelect?: (item: Doc<"menu_items">) => void;
}

export function MenuItemCard({
  item,
  priority = false,
  quickAddRequiredIds = null,
  onSelect,
}: MenuItemCardProps) {
  const [showCustomization, setShowCustomization] = useState(false);
  const useCart = useActiveCartStore();
  const addItem = useCart((state) => state.addItem);

  // Prefer the shared surface-level sheet; fall back to a local one when no
  // onSelect is wired (keeps stand-alone consumers like the catering page
  // working without every card statically importing the sheet).
  const openCustomization = () => {
    if (onSelect) {
      onSelect(item);
      return;
    }
    setShowCustomization(true);
  };

  const imageUrl = item.photoUrl
    ? optimizeCloudinaryUrl(item.photoUrl, 300)
    : null;
  const priceLabel = formatMenuItemPriceLabel(item);

  // One-tap add is only for items with zero required choices; anything with a
  // required template selection or required modifier group keeps the sheet.
  const quickAddEnabled =
    quickAddRequiredIds != null &&
    !itemRequiresCustomization(item, quickAddRequiredIds);

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!quickAddEnabled) {
      openCustomization();
      return;
    }

    const priceInCents = Math.round(item.price * 100);
    addItem({
      menuItemId: item._id,
      name: item.name,
      price: priceInCents,
      quantity: 1,
      modifiers: [],
      photoUrl: item.photoUrl || undefined,
    });
    toast.success(`${item.name} added to your order`, {
      description: formatPrice(priceInCents),
      duration: 2000,
    });
  };

  const addButtonLabel = quickAddEnabled
    ? `Add ${item.name} to your order`
    : `Customize ${item.name}`;

  return (
    <>
      <div
        className={cn(
          "group relative h-full min-h-[172px] overflow-hidden",
          "rounded-[12px] border border-[#ededed] bg-white transition-[border-color,box-shadow,transform] duration-150",
          "hover:-translate-y-0.5 hover:border-[#d6d6d6] hover:shadow-[0_14px_32px_-24px_rgb(0_0_0_/_0.45)]",
          "active:scale-[0.985]"
        )}
      >
        <button
          type="button"
          onClick={openCustomization}
          aria-label={`View ${item.name}`}
          className="absolute inset-0 z-10 cursor-pointer rounded-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
        />
        <div className="flex h-full items-start gap-3 p-4">
          {/* Details */}
          <div className="flex min-h-[140px] min-w-0 flex-1 flex-col">
            <div className="mb-1.5 flex items-start justify-between gap-3">
              <h3 className="text-[17px] font-bold leading-[1.25] tracking-normal text-[#0d0d0d] transition-colors group-hover:text-[var(--roma-terracotta)]">
                {item.name}
              </h3>
            </div>
            {item.description ? (
              <p className="mb-3 line-clamp-2 text-[14px] font-medium leading-5 text-[#575757] sm:line-clamp-3">
                {item.description}
              </p>
            ) : (
              <div className="flex-1" />
            )}

            <div className="mt-auto flex items-center justify-between gap-2">
              <span className="text-[15px] font-bold tabular-nums leading-5 text-[#0d0d0d]">
                {priceLabel}
              </span>
              {!imageUrl && (
                <button
                  onClick={handleAddClick}
                  className="relative z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0d0d0d] shadow-[0_1px_3px_rgb(0_0_0_/_0.18),0_0_0_1px_rgb(0_0_0_/_0.08)] transition-transform duration-150 hover:scale-105 active:scale-95"
                  aria-label={addButtonLabel}
                >
                  <Plus className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Image */}
          {imageUrl && (
            <div className="relative h-36 w-36 shrink-0 overflow-hidden rounded-[10px] bg-[#f4f4f5] ring-1 ring-[#ededed]">
              <Image
                src={imageUrl}
                alt={item.name}
                fill
                priority={priority}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : undefined}
                className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
                sizes="144px"
              />
              <button
                onClick={handleAddClick}
                className="absolute bottom-2 right-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0d0d0d] shadow-[0_1px_3px_rgb(0_0_0_/_0.18),0_0_0_1px_rgb(0_0_0_/_0.08)] transition-transform duration-150 hover:scale-105 active:scale-95"
                aria-label={addButtonLabel}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Local fallback sheet — only mounts when this card owns its own sheet
          (no onSelect wired) and the guest has actually opened it. */}
      {onSelect ? null : (
        <ItemCustomizationSheet
          item={item}
          open={showCustomization}
          onOpenChange={setShowCustomization}
        />
      )}
    </>
  );
}
